import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import CTASection from '../components/CTASection';
import { CheckCircle2, ArrowRight, Calculator, FileText, CreditCard, PieChart, TrendingUp, Building } from 'lucide-react';

const ZohoBooks = () => {
  const features = [
    {
      icon: <FileText className="w-6 h-6 text-blue-700" />,
      title: 'Facturatie',
      description: 'Professionele facturen maken en versturen in enkele klikken.',
    },
    {
      icon: <CreditCard className="w-6 h-6 text-blue-700" />,
      title: 'Betalingen',
      description: 'Ontvang online betalingen en houd debiteuren bij.',
    },
    {
      icon: <Calculator className="w-6 h-6 text-blue-700" />,
      title: 'Boekhouding',
      description: 'Complete boekhouding volgens Nederlandse richtlijnen.',
    },
    {
      icon: <PieChart className="w-6 h-6 text-blue-700" />,
      title: 'Financiële Rapportages',
      description: 'Realtime inzicht in uw financiële positie.',
    },
    {
      icon: <TrendingUp className="w-6 h-6 text-blue-700" />,
      title: 'BTW Aangifte',
      description: 'Automatische BTW berekening en rapportages.',
    },
    {
      icon: <Building className="w-6 h-6 text-blue-700" />,
      title: 'Bankkoppelingen',
      description: 'Automatische import van banktransacties.',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden bg-gradient-to-br from-blue-50 via-white to-blue-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-6">
                Online Boekhouden
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                Zoho Books: Professioneel Boekhouden Voor Ondernemers
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Houd uw financiën overzichtelijk met Zoho Books. Online boekhoudsoftware die voldoet aan 
                Nederlandse wet- en regelgeving en naadloos integreert met uw andere systemen.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact">
                  <Button 
                    size="lg" 
                    className="bg-blue-700 hover:bg-blue-800 text-white px-8 py-6 text-lg font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 group"
                  >
                    Vraag een Demo Aan
                    <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                  </Button>
                </Link>
                
                <a href="tel:0638823471">
                  <Button 
                    size="lg" 
                    variant="outline"
                    className="border-2 border-blue-700 text-blue-700 hover:bg-blue-50 px-8 py-6 text-lg font-semibold rounded-lg transition-all duration-300"
                  >
                    Bel: 06 3882 3471
                  </Button>
                </a>
              </div>
            </div>

            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1666875753105-c63a6f3bdc86?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwzfHxkYXNoYm9hcmR8ZW58MHx8fHwxNzczODE3NjA4fDA&ixlib=rb-4.1.0&q=85"
                alt="Zoho Books Dashboard"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* What is Zoho Books Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Wat is Zoho Books?
            </h2>
            <div className="prose prose-lg text-gray-600 space-y-4">
              <p>
                Zoho Books is een complete online boekhoudsoftware die speciaal is ontwikkeld voor kleine en 
                middelgrote ondernemingen. Met Zoho Books krijgt u controle over uw financiën, automatiseert u 
                facturatie en krijgt u realtime inzicht in uw bedrijfsresultaten.
              </p>
              <p>
                Als Zoho Partner helpt Guijs Consultancy u met de implementatie van Zoho Books volgens Nederlandse 
                boekhoudstandaarden. Wij zorgen voor de juiste configuratie van BTW tarieven, grootboekrekeningen 
                en rapportages die voldoen aan de eisen van de Belastingdienst.
              </p>
              <p>
                Zoho Books integreert naadloos met andere Zoho producten zoals Zoho CRM en Zoho Inventory, maar ook 
                met banken en andere externe systemen. Dit zorgt voor één centrale plek waar al uw financiële data 
                samenkomt.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Krachtige Functies van Zoho Books
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Van facturatie tot financiële rapportages - alles in één systeem.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card key={`item-${index}`} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                <CardContent className="p-6">
                  <div className="mb-4">{feature.icon}</div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Voordelen van Zoho Books
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Zoho Books bespaart u tijd en geld door uw boekhoudprocessen te automatiseren en te vereenvoudigen.
              </p>

              <ul className="space-y-4">
                {[
                  'Bespaar uren op facturatie en administratie',
                  'Verminder fouten met automatische berekeningen',
                  'Krijg realtime inzicht in uw financiële positie',
                  'Voldoe automatisch aan Nederlandse wet- en regelgeving',
                  'Werk samen met uw accountant via één platform',
                  'Schaal mee met uw bedrijfsgroei',
                ].map((benefit, index) => (
                  <li key={`item-${index}`} className="flex items-start space-x-3">
                    <CheckCircle2 className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 text-lg">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <img
                src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHw0fHxidXNpbmVzcyUyMGNvbnN1bHRpbmd8ZW58MHx8fHwxNzczNzQ2NTMwfDA&ixlib=rb-4.1.0&q=85"
                alt="Financial Growth"
                className="rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Waarom Guijs Consultancy voor Zoho Books?
            </h2>
            <p className="text-xl text-gray-600 mb-12">
              Wij zorgen ervoor dat Zoho Books perfect aansluit op uw financiële processen en Nederlandse regelgeving.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-left">
              <Card className="border-2 border-blue-200 shadow-lg">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Nederlandse Configuratie</h3>
                  <p className="text-gray-600">
                    Wij configureren Zoho Books volgens Nederlandse boekhoudstandaarden en BTW richtlijnen.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 border-blue-200 shadow-lg">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Bankkoppelingen</h3>
                  <p className="text-gray-600">
                    Automatische import van banktransacties van alle Nederlandse banken.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 border-blue-200 shadow-lg">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">CRM Integratie</h3>
                  <p className="text-gray-600">
                    Naadloze koppeling met Zoho CRM voor automatische facturatie vanuit sales.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 border-blue-200 shadow-lg">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Training & Support</h3>
                  <p className="text-gray-600">
                    Complete training voor uw team en doorlopende ondersteuning.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTASection 
        title="Klaar voor Professioneel Boekhouden?"
        subtitle="Ontdek hoe Zoho Books uw financiële administratie kan vereenvoudigen."
      />
    </div>
  );
};

export default ZohoBooks;
